import React, { useState, useEffect } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import PersonalInfoTab from './components/PersonalInfoTab';
import SecurityTab from './components/SecurityTab';
import NotificationsTab from './components/NotificationsTab';

import sessionService from '../../utils/sessionService.js';
import { useUserProfile } from '../../hooks/useUserProfile';
import useToast from '../../hooks/useToast';


const UserProfileSettings = () => {
  const [activeTab, setActiveTab] = useState('personal');

  // Use centralized user profile hook
  const {
    userProfile,
    currentOrganization,
    loading: profileLoading,
    updateUserProfile,
    refreshProfile,
  } = useUserProfile();

  // Toast notifications
  const { showToast, hideToast } = useToast();

  // Legacy state for compatibility with existing components
  const [userRole, setUserRole] = useState('member');
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Real user data from backend
  const [userData, setUserData] = useState({
    fullName: '',
    email: '',
    jobTitle: '',
    bio: '',
    avatar: '',
  });

  // Sync with hook data
  useEffect(() => {
    if (userProfile) {
      setCurrentUser(userProfile);
      setUserRole(
        userProfile.role ||
          userProfile.organizationRole ||
          sessionService.getUserRole() ||
          'member'
      );

      setUserData({
        fullName: userProfile.displayName || '',
        email: userProfile.email || '',
        jobTitle: userProfile.jobTitle || '',
        bio: userProfile.bio || '',
        avatar: userProfile.avatar || '',
        lastUpdated: (userProfile.updatedAt || userProfile.updated_at || userProfile.updated)
          ? new Date(userProfile.updatedAt || userProfile.updated_at || userProfile.updated).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) : 'Never',
      });
    }
    setLoading(profileLoading);
  }, [userProfile, profileLoading]);

  const tabs = [
    {
      id: 'personal',
      label: 'Personal Info',
      icon: 'User',
      description: 'Manage your profile information',
    },
    {
      id: 'security',
      label: 'Security',
      icon: 'Shield',
      description: 'Password and active sessions',
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: 'Bell',
      description: 'Email and in-app notification preferences',
    },

  ];

  const handlePersonalInfoSave = async (formData) => {
    try {
      console.log('🔄 Starting profile update with data:', formData);

      // Import realApiService dynamically to avoid circular imports
      const { default: realApiService } = await import('../../utils/realApiService.js');

      // Ensure the service is available
      if (!realApiService) {
        throw new Error('API service is not available');
      }

      // Try users.updateProfile first (correct method), fallback to auth.updateProfile
      let result;
      if (realApiService.users && typeof realApiService.users.updateProfile === 'function') {
        result = await realApiService.users.updateProfile(formData);
      } else if (realApiService.auth && typeof realApiService.auth.updateProfile === 'function') {
        result = await realApiService.auth.updateProfile(formData);
      } else {
        throw new Error('Profile update method is not available');
      }

      if (result.error) {
        throw new Error(result.error);
      }

      // Update local state with the response data
      // Backend returns user profile directly, not wrapped in 'data'
      const updatedUser = result.data || result;

      // Get the updated timestamp from backend response, or use current time
      const updatedTimestamp = updatedUser.updated_at || updatedUser.updatedAt || updatedUser.updated || new Date().toISOString();

      const newUserData = {
        fullName: `${updatedUser.first_name || ''} ${
          updatedUser.last_name || ''
        }`.trim(),
        email: updatedUser.email || userData.email,
        jobTitle: updatedUser.job_title || '',
        bio: updatedUser.bio || '',
        avatar: updatedUser.profile_picture || userData.avatar,
        lastUpdated: new Date(updatedTimestamp).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
      };

      setUserData(newUserData);

      // Update current user state
      setCurrentUser((prev) => ({
        ...prev,
        ...updatedUser,
      }));

      // Update the global user profile state for immediate header updates
      const profileUpdates = {
        first_name: updatedUser.first_name,
        last_name: updatedUser.last_name,
        displayName: `${updatedUser.first_name || ''} ${updatedUser.last_name || ''}`.trim(),
        name: `${updatedUser.first_name || ''} ${updatedUser.last_name || ''}`.trim(),
        job_title: updatedUser.job_title,
        bio: updatedUser.bio,
        email: updatedUser.email,
      };
      updateUserProfile(profileUpdates);

      // Persist into session so header and other consumers update immediately
      try {
        const existing = sessionService.getCurrentUser() || {};
        const merged = { ...existing, ...updatedUser };
        // Normalize displayName
        merged.displayName = `${merged.first_name || ''} ${merged.last_name || ''}`.trim() || merged.displayName || existing.displayName;
        sessionService.user = merged;
        sessionService.storeSession();
      } catch (e) {
        console.warn('Session snapshot update failed (non-blocking):', e);
      }

      // Refresh the profile to ensure consistency
      await refreshProfile();

      // Show success toast notification
      showToast('Profile updated successfully', 'success', 4000);

      console.log('Personal info updated successfully:', result.data);
    } catch (error) {
      console.error('Failed to update personal info:', error);

      // Show error toast notification
      showToast(
        error.message || 'Failed to update profile. Please try again.',
        'error',
        5000
      );

      throw error; // Re-throw to let the component handle the error
    }
  };

  const handlePasswordChange = async (passwordData) => {
    try {
      console.log('🔄 Changing password:', { ...passwordData, currentPassword: '[HIDDEN]', newPassword: '[HIDDEN]' });

      // Import realApiService dynamically to avoid circular imports
      const { default: realApiService } = await import('../../utils/realApiService');

      // Update password via API
      const result = await realApiService.auth.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });

      console.log('✅ Password changed successfully');

      // Refresh the profile to ensure consistency
      await refreshProfile();

      // Show success toast notification
      showToast('Password updated successfully', 'success', 4000);

      return result;
    } catch (error) {
      console.error('❌ Failed to change password:', error);

      // Show error toast notification
      showToast(
        error.message || 'Failed to update password. Please try again.',
        'error',
        5000
      );

      throw error; // Re-throw to let the component handle the error
    }
  };

  const handleTwoFactorToggle = (enabled) => {
    console.log('Two-factor authentication:', enabled ? 'enabled' : 'disabled');
  };

  const handleNotificationsSave = async (notificationSettings) => {
    try {
      console.log('🔄 Saving notification settings:', notificationSettings);

      // Import realApiService dynamically to avoid circular imports
      const { default: realApiService } = await import('../../utils/realApiService');

      // Update notification preferences via API
      // Send the full nested object to support rich preferences (quiet hours, projects)
      const result = await realApiService.notifications.updatePreferences(notificationSettings);

      console.log('✅ Notification preferences updated successfully:', result);
    } catch (error) {
      console.error('❌ Failed to update notification preferences:', error);
      throw error; // Re-throw to let the component handle the error
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return (
          <PersonalInfoTab
            key={`personal-${userData.lastUpdated || 'initial'}`}
            userData={userData}
            onSave={handlePersonalInfoSave}
          />
        );
      case 'security':
        return (
          <SecurityTab
            onPasswordChange={handlePasswordChange}
            onTwoFactorToggle={handleTwoFactorToggle}
            initialTwoFactorEnabled={userProfile?.two_factor_enabled || false}
          />
        );
      case 'notifications':
        return <NotificationsTab onSave={handleNotificationsSave} organizationId={currentOrganization?.id || sessionService.getOrganizationId()} />;
      default:
        return null;
    }
  };

  return (
    <div className='min-h-screen bg-background'>
      <RoleBasedHeader
        userRole={userRole.toLowerCase()}
        currentUser={
          userProfile
            ? {
                name: userProfile.displayName,
                displayName: userProfile.displayName,
                email: userProfile.email,
                role: userProfile.role,
              }
            : null
        }
        currentOrganization={currentOrganization}
      />

      <main className='pt-16'>
        <div className='max-w-6xl mx-auto px-6 py-8'>
          <Breadcrumb />

          {/* Page Header */}
          <div className='mb-8'>
            <div className='flex items-center space-x-3 mb-2'>
              <div className='w-10 h-10 bg-primary rounded-lg flex items-center justify-center'>
                <Icon
                  name='Settings'
                  size={20}
                  className='text-primary-foreground'
                />
              </div>
              <div>
                <h1 className='text-3xl font-bold text-foreground'>
                  Profile Settings
                </h1>
                <p className='text-muted-foreground'>
                  Manage your account settings and preferences
                </p>
              </div>
            </div>
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-4 gap-8'>
            {/* Sidebar Navigation */}
            <div className='lg:col-span-1'>
              <div className='bg-card border border-border rounded-lg p-1 sticky top-24'>
                <nav className='space-y-1'>
                  {tabs
                    .filter(t => !t.roles || t.roles.includes((userRole||'').toLowerCase()))
                    .map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-smooth ${
                          activeTab === tab.id
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={tab.icon} size={18} />
                        <div className='flex-1 min-w-0'>
                          <div className='font-medium'>{tab.label}</div>
                          <div className={`text-xs mt-0.5 ${activeTab === tab.id ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                            {tab.description}
                          </div>
                        </div>
                      </button>
                  ))}
                </nav>
              </div>

              {/* Mobile Tab Selector */}
              <div className='lg:hidden mb-6'>
                <select
                  value={activeTab}
                  onChange={(e) => setActiveTab(e.target.value)}
                  className='w-full px-4 py-3 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent'
                >
                  {tabs
                    .filter(t => !t.roles || t.roles.includes((userRole||'').toLowerCase()))
                    .map((tab) => (
                      <option key={tab.id} value={tab.id}>
                        {tab.label}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {/* Main Content */}
            <div className='lg:col-span-3'>
              <div className='bg-card border border-border rounded-lg p-8'>
                {/* Tab Header */}
                <div className='mb-8 pb-6 border-b border-border'>
                  <div className='flex items-center space-x-3'>
                    <Icon
                      name={tabs.find((tab) => tab.id === activeTab)?.icon || 'Settings'}
                      size={24}
                      className='text-primary'
                    />
                    <div>
                      <h2 className='text-2xl font-semibold text-foreground'>
                        {tabs.find((tab) => tab.id === activeTab)?.label}
                      </h2>
                      <p className='text-muted-foreground'>
                        {tabs.find((tab) => tab.id === activeTab)?.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tab Content */}
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Toast Notifications handled globally by ToastProvider */}
    </div>
  );
};

export default UserProfileSettings;
